import { useState } from "react";
import { message } from "antd";
import {
  getParkings,
  createParking,
  updateParking,
  createParkingLine,
  archiveParking,
  unarchiveParking,
} from "../../../api/parkpass/parkpassParkings";

export const useParkingManagement = (form) => {
  const [parkings, setParkings] = useState([]);
  const [currentParking, setCurrentParking] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [editingParking, setEditingParking] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [zoomToParking, setZoomToParking] = useState(null);

  const fetchParkings = async () => {
    try {
      setLoading(true);
      const data = await getParkings();
      setParkings(data);
    } catch (error) {
      message.error("Échec de la récupération des parkings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePolygonComplete = (data) => {
    setCurrentParking(data);
    setIsModalVisible(true);
    setDrawingMode(false);
  };

  const startEditingParking = (parking) => {
    setEditingParking({
      ...parking,
      coordinates: [...parking.coordinates],
    });
    setCurrentParking({
      coordinates: [...parking.coordinates],
      marker: parking.marker,
    });
    setIsEditing(true);
    setDrawingMode(false);
  };

  const handlePolygonEdit = (newCoordinates) => {
    setCurrentParking((prev) => ({
      ...prev,
      coordinates: [...newCoordinates],
    }));
    setEditingParking((prev) => ({
      ...prev,
      coordinates: [...newCoordinates],
    }));
  };

  const handleMarkerEdit = (newMarkerPosition) => {
    setCurrentParking((prev) => ({
      ...prev,
      marker: newMarkerPosition,
    }));
    setEditingParking((prev) => ({
      ...prev,
      marker: newMarkerPosition,
    }));
  };

  const handleViewParking = (parking) => {
    setZoomToParking(parking);
    setTimeout(() => setZoomToParking(null), 1000);
  };

  const cancelEditing = () => {
    setEditingParking(null);
    setCurrentParking(null);
    setIsEditing(false);
    setDrawingMode(true);
    form.resetFields();
    fetchParkings();
  };

  const handleSave = async (values) => {
    // Convert tariff array to object format
    const tariffObject = {};
    if (values.tariff_info && Array.isArray(values.tariff_info)) {
      values.tariff_info.forEach(item => {
        if (item && item.key && item.value) {
          tariffObject[item.key] = item.value;
        }
      });
    }

    const parkingData = {
      name: values.name,
      name_ar: values.name_ar,
      name_fr: values.name_fr,
      parking_type: values.parking_type,
      external_id: values.external_id,
      tariff_info: tariffObject,
      notes: values.notes,
      archived: values.archived || false,
    };

    // Handle coordinates and marker based on user selection
    if (currentParking) {
      // Add coordinates only if user wants to save polygon and data exists
      if (values.save_polygon !== false && currentParking.coordinates && currentParking.coordinates.length > 0) {
        parkingData.coordinates = currentParking.coordinates.map(coord => ({
          lat: coord.lat,
          lng: coord.lng
        }));
      }
      
      // Add marker only if user wants to save marker and data exists
      if (values.save_marker !== false && currentParking.marker) {
        parkingData.marker = currentParking.marker;
      } else if (values.save_marker !== false && !currentParking.marker && currentParking.coordinates && currentParking.coordinates.length > 0) {
        // Fallback: use first coordinate as marker if marker not explicitly set
        parkingData.marker = {
          lat: currentParking.coordinates[0].lat,
          lng: currentParking.coordinates[0].lng
        };
      }
    }

    try {
      setLoading(true);
      let savedParking;
      
      if (editingParking) {
        savedParking = await updateParking(editingParking.id, parkingData);
        message.success("Parking mis à jour avec succès");
      } else {
        savedParking = await createParking(parkingData);
        message.success("Parking créé avec succès");
      }

      // Handle lines if provided
      if (values.lines && Array.isArray(values.lines) && values.lines.length > 0) {
        const parkingId = savedParking.id;
        
        // Create lines for the parking
        for (const line of values.lines) {
          try {
            await createParkingLine({
              external_id: String(line.external_id),
              name: line.name,
              parking_id: parkingId,
            });
          } catch (lineError) {
            console.error("Error creating line:", lineError);
            message.warning(`Erreur lors de la création de la ligne: ${line.name}`);
          }
        }
      }

      await fetchParkings();
      handleModalClose();
    } catch (error) {
      message.error(
        editingParking
          ? "Échec de la mise à jour du parking"
          : "Échec de l'enregistrement du parking"
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setCurrentParking(null);
    setEditingParking(null);
    setIsEditing(false);
    setDrawingMode(true);
    form.resetFields();
  };

  const handleArchive = async (parkingId) => {
    try {
      setLoading(true);
      await archiveParking(parkingId);
      message.success("Parking archivé avec succès");
      await fetchParkings();
    } catch (error) {
      message.error("Échec de l'archivage du parking");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (parkingId) => {
    try {
      setLoading(true);
      await unarchiveParking(parkingId);
      message.success("Parking désarchivé avec succès");
      await fetchParkings();
    } catch (error) {
      message.error("Échec du désarchivage du parking");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    parkings,
    currentParking,
    isModalVisible,
    loading,
    drawingMode,
    editingParking,
    isEditing,
    zoomToParking,
    fetchParkings,
    handlePolygonComplete,
    startEditingParking,
    handlePolygonEdit,
    handleMarkerEdit,
    handleViewParking,
    cancelEditing,
    handleSave,
    handleModalClose,
    handleArchive,
    handleUnarchive,
    setDrawingMode,
    setIsModalVisible,
  };
};
